/**
 * Utility functions for JWT token handling
 */

/**
 * Decode JWT token without verification (client-side only)
 * @param {string} token - JWT token string
 * @returns {object|null} Decoded payload or null if invalid
 */
export function decodeJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) {
      return null;
    }
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Get expiration date from JWT token
 * @param {string} token - JWT token string
 * @returns {Date|null} Expiration date or null if invalid
 */
export function getJwtExpiration(token) {
  const decoded = decodeJwt(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  // JWT exp is in seconds, convert to milliseconds
  return new Date(decoded.exp * 1000);
}

/**
 * Check if JWT token is expiring soon
 * @param {string} token - JWT token string
 * @param {number} daysThreshold - Number of days before expiration to consider "soon" (default: 7)
 * @returns {boolean} True if token expires within threshold
 */
export function isJwtExpiringSoon(token, daysThreshold = 7) {
  if (!token) {
    return true; // No token means we should check
  }
  
  const expiration = getJwtExpiration(token);
  if (!expiration) {
    return true; // Invalid token means we should check
  }
  
  const now = new Date();
  const daysUntilExpiration = (expiration.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  
  return daysUntilExpiration <= daysThreshold;
}

/**
 * Get JWT token from cookies
 * @param {string} cookieName - Name of the cookie (default: 'jwtToken')
 * @returns {string|null} JWT token or null if not found
 */
export function getJwtFromCookie(cookieName = 'jwtToken') {
  if (typeof document === 'undefined') {
    return null; // Server-side rendering
  }
  
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

